import { PaginatedApiArg } from 'lib-shared-api-utils';
import { useCallback, useEffect, useState } from 'react';


import {
  ConversationsPaginatedFilterSearch,
  UseConversationsFilterSearchOptions,
  UseConversationsFilterSearchResult,
} from './useConversationsPaginatedFilterSearch.types';

/** The search debounce time in milliseconds. */
const searchDebounceTime = 300;

/**
 * Represents the hook for handling search in a conversations filter that relies
 * on a paginated endpoint.
 */
export function useConversationsPaginatedFilterSearch<
  TPaginatedApiArg extends PaginatedApiArg,
>({
  arg,
  changeArg,
}: UseConversationsFilterSearchOptions<TPaginatedApiArg>): UseConversationsFilterSearchResult {
  const [search, setSearch] = useState<ConversationsPaginatedFilterSearch>({});

  /** Handles searching the items in the dropdown select. */
  const handleSearch = useCallback((value?: string) => {
    setSearch({ term: value?.trim() || undefined });
  }, []);

  /** Handles when the dropdown open state is toggled. */
  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setSearch({});
      }
    },
    [setSearch],
  );

  // Changes the arg when search term changes.
  useEffect(() => {
    if (search.term !== arg?.searchTerm) {
      changeArg(
        {
          searchTerm: search.term,
          offset: undefined,
          limit: undefined,
        } as Exclude<TPaginatedApiArg, string>,
        { debounceTime: search?.debounceTime ?? searchDebounceTime },
      );
    }
    // Don't rely on arg as dependency because we change the arg inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeArg, search]);

  return {
    search,
    handleToggle,
    handleSearch,
  };
}
